/**
 * Split-result detection for a forced-choice insight.
 *
 * All that survives of the Quick Study → Conversations hand-off module: the converse /
 * debate room assembly it existed for has no caller left, and `InsightCard` is the one
 * remaining consumer of these two helpers.
 *
 * Pure / no React so it stays unit-testable in isolation.
 */

const Q_NAME = 'qs_main'

export interface ConverseQuestion {
  type?: string
  name?: string
  title?: string
  choices?: Array<{ value?: unknown; text?: string }>
  [k: string]: unknown
}

export interface ConverseResponse {
  persona_id?: string
  survey_data: Record<string, unknown>
  attributes?: Record<string, unknown>
}

export interface ConverseInsight {
  title?: string
  headline?: string
  question?: ConverseQuestion
  responses?: ConverseResponse[]
  /** QS-7 agreement (0..1). Used by `isSplitResult` to detect a near-tie. */
  agreement?: number
  /** Optional human spread note ("Split" / "Mixed" …) — surfaced in the framing. */
  spread_note?: string
  /** Forced-choice winning option label, when known. */
  headlineOptionLabel?: string
}

/** Whether a result is genuinely split: an explicit spread note, or low agreement. */
export function isSplitResult(insight: ConverseInsight | undefined): boolean {
  if (!insight) return false
  const note = (insight.spread_note ?? '').toLowerCase()
  if (note.includes('split') || note.includes('mixed')) return true
  if (typeof insight.agreement === 'number') return insight.agreement <= 0.6
  return false
}

/** The two leading option labels for a forced-choice insight, by answer count.
 *  Returns up to two {label,count}; used to frame pro-A / pro-B. */
export function topTwoOptions(insight: ConverseInsight | undefined): Array<{ label: string; count: number }> {
  if (!insight?.responses?.length) return []
  const q = insight.question
  const counts = new Map<string, number>()
  for (const r of insight.responses) {
    const raw = r.survey_data?.[Q_NAME]
    if (raw == null) continue
    const vals = Array.isArray(raw) ? raw : [raw]
    for (const val of vals) {
      const choice = q?.choices?.find((c) => String(c.value) === String(val))
      const label = (choice?.text ?? String(val)).trim()
      if (!label) continue
      counts.set(label, (counts.get(label) ?? 0) + 1)
    }
  }
  return [...counts.entries()]
    .map(([label, count]) => ({ label, count }))
    .sort((a, b) => b.count - a.count)
    .slice(0, 2)
}
